
import React, { jsx, useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiMail,
  FiArrowLeft,
  FiHome,
  FiUser,
  FiLock,
  FiSend,
} = FiIcons;

const Login = () => {
  const navigate = useNavigate();

  const {
    login,
    verifyLoginOtp,
    signUp,
    user,
    isAuthenticated,
  } = useAuth();

  const [isLogin, setIsLogin] = useState(true);

  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');

  const [otpSent, setOtpSent] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (isAuthenticated && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const handleSendOtp = async (e) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your email');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const result = await login(email);

      if (result.success) {
        setOtpSent(true);
        setMessage(
          'OTP sent successfully. Please check your email.'
        );
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    if (!otp) {
      setError('Please enter OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await verifyLoginOtp(email, otp);

      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.error || 'Invalid OTP');
      }
    } catch (err) {
      setError('OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();

    if (!fullName || !email || !password) {
      setError('Please fill all fields');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const result = await signUp(email, password, {
        full_name: fullName,
        role: 'user',
        email,
      });

      if (result.success) {
        setMessage(
          'Account created successfully. Please verify your email.'
        );

        setEmail('');
        setPassword('');
        setFullName('');
        setIsLogin(true);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
  <div className="min-h-screen flex bg-gradient-to-br from-indigo-900 via-blue-800 to-cyan-700">
    
    {/* Left Branding */}
    <div className="hidden lg:flex w-1/2 flex-col justify-center px-16 text-white">
      <div className="max-w-lg">
        <div className="inline-block bg-white rounded-xl p-2 shadow-lg">
  <img
    src="/images/NRI_Logo.svg"
    alt="NRI Local Buddy"
    className="h-20"
  />
</div>

        <h1 className="text-5xl font-bold leading-tight mb-6">
          Stay Connected
          <span className="block text-cyan-300">
            With Home
          </span>
        </h1>

        <p className="text-lg text-blue-100 mb-10">
          Your trusted partner for property management,
          parental care, travel assistance, celebrations,
          and local support services across India.
        </p>

        <div className="space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-cyan-400"></div>
            <span>Secure OTP Authentication</span>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-cyan-400"></div>
            <span>Track Service Requests</span>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-cyan-400"></div>
            <span>Manage Everything From One Dashboard</span>
          </div>
        </div>
      </div>
    </div>

    {/* Right Side */}
    <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
      <motion.div
        initial={{ opacity: 0, y: 25 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/30 p-6 sm:p-8">

          {/* Back Button */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-slate-500 hover:text-indigo-600 mb-6 text-sm sm:text-base"
          >
            <SafeIcon icon={FiArrowLeft} />
            <span>Back to Home</span>
          </Link>

          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-block bg-white rounded-xl p-1">
  <img
    src="/images/NRI_Logo.svg"
    alt="NRI Local Buddy"
    className="h-14 sm:h-20"
  />
</div>

            <h2 className="text-3xl font-bold text-slate-800">
              {isLogin ? "Welcome Back" : "Create Account"}
            </h2>

            <p className="text-slate-500 mt-2">
              {isLogin
                ? "Login securely using OTP"
                : "Join NRI Local Buddy today"}
            </p>
          </div>

          {/* Alerts */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl px-4 py-3 mb-5">
              {error}
            </div>
          )}

          {message && (
            <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 mb-5">
              {message}
            </div>
          )}

          {/* OTP Steps */}
          {isLogin && (
            <div className="flex items-center justify-center mb-8">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                  !otpSent
                    ? "bg-indigo-600 text-white"
                    : "bg-green-500 text-white"
                }`}
              >
                1
              </div>

              <div className="w-16 h-1 bg-slate-200"></div>

              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                  otpSent
                    ? "bg-indigo-600 text-white"
                    : "bg-slate-200 text-slate-500"
                }`}
              >
                2
              </div>
            </div>
          )}

          {/* Login Form */}
          {isLogin ? (
            <form
              onSubmit={
                otpSent
                  ? handleVerifyOtp
                  : handleSendOtp
              }
              className="space-y-5"
            >
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email Address
                </label>

                <div className="relative">
                  <SafeIcon
                    icon={FiMail}
                    className="absolute left-4 top-4 text-slate-400"
                  />

                  <input
                    type="email"
                    value={email}
                    disabled={otpSent}
                    onChange={(e) =>
                      setEmail(e.target.value)
                    }
                    className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              {otpSent && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Verification Code
                  </label>

                  <input
                    type="text"
                    value={otp}
                    onChange={(e) =>
                      setOtp(e.target.value)
                    }
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
                    placeholder="Enter OTP"
                    required
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 text-white py-3 rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50"
              >
                {loading
                  ? otpSent
                    ? "Verifying..."
                    : "Sending OTP..."
                  : otpSent
                    ? "Verify OTP"
                    : "Send OTP"}
              </button>

              {otpSent && (
                <button
                  type="button"
                  onClick={handleSendOtp}
                  className="w-full text-indigo-600 font-medium"
                >
                  Resend OTP
                </button>
              )}
            </form>
          ) : (
            <form
              onSubmit={handleSignUp}
              className="space-y-5"
            >
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Full Name
                </label>

                <input
                  type="text"
                  value={fullName}
                  onChange={(e) =>
                    setFullName(e.target.value)
                  }
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email
                </label>

                <input
                  type="email"
                  value={email}
                  onChange={(e) =>
                    setEmail(e.target.value)
                  }
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Password
                </label>

                <input
                  type="password"
                  value={password}
                  onChange={(e) =>
                    setPassword(e.target.value)
                  }
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 text-white py-3 rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50"
              >
                {loading
                  ? "Creating Account..."
                  : "Create Account"}
              </button>
            </form>
          )}

          <div className="mt-8 pt-6 border-t text-center">
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setError("");
                setMessage("");
                setOtpSent(false);
              }}
              className="text-indigo-600 font-medium hover:text-indigo-700"
            >
              {isLogin
                ? "Don't have an account? Sign Up"
                : "Already have an account? Login"}
            </button>
          </div>

        </div>
      </motion.div>
    </div>
  </div>
);
};

export default Login;